import React, { useState, useEffect } from "react";
import useAxios from "axios-hooks";
import {
    Spinner,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField,
    Toggle
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import ActionPlanTipTable from "./ActionPlanTipTable";
import { Form, Formik } from "formik";
import CustomTextField from "../../../../Forms/TextField";
import CustomToggle from "../../../../Forms/Toggle";
import PropTypes from "prop-types";

const ActionPlanTipList = ({ crsId, revId }) => {
    const [selectedTipId, setSelectedTipId] = useState(undefined);

    const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);
    const [showCreateModal, { toggle: toggleCreateModal }] = useBoolean(false);
    const [showUpdateModal, { toggle: toggleUpdateModal }] = useBoolean(false);

    const [{ data: course, loading }, refetch] = useAxios(`courses/${crsId}/${revId || ''}`);

    if (loading)
        return <Spinner className="absolute-center" label="Loading data..." />;

    return (
        <>
            <ActionPlanTipTable
                tips={course.tips}
                setSelectedTipId={setSelectedTipId}
                selectedTipId={selectedTipId}
                createOnClick={() => toggleCreateModal()}
                deleteOnClick={(tip) => {
                    if (tip && tip.crsActionPlanTipID !== undefined) {
                        setSelectedTipId(tip.crsActionPlanTipID);
                        toggleDeleteModal();
                    } else if (selectedTipId) {
                        toggleDeleteModal();
                    }
                }}
                editOnClick={(tip) => {
                    setSelectedTipId(tip.crsActionPlanTipID);
                    toggleUpdateModal();
                }}
                loading={loading}
                createDisabled={revId && revId != course.currentCRSRevID}
            />
            <CreateTipModal
                courseId={crsId}
                showModal={showCreateModal}
                toggleShowModal={toggleCreateModal}
                onCreate={() => refetch()}
            />
            <UpdateTipModal
                courseId={crsId}
                course={course}
                selectedTipId={selectedTipId}
                showModal={showUpdateModal}
                toggleShowModal={toggleUpdateModal}
                onUpdate={() => refetch()}
            />
            <DeleteTipModal
                courseId={crsId}
                selectedTipId={selectedTipId}
                showModal={showDeleteModal}
                toggleShowModal={toggleDeleteModal}
                onDelete={() => refetch()}
            />
        </>
    );
};

const CreateTipModal = ({
    showModal,
    toggleShowModal,
    onCreate,
    courseId
}) => {
    const [{ loading }, postForm] = useAxios(
        {
            method: "post",
            url: `/courses/${courseId}/action-plan-tip`,
        },
        { manual: true }
    );
    if (loading)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Create",
                subText: "Tip"
            }}
        >
            <Formik
                initialValues={{}}
                onSubmit={async (values) => {
                    const result = await postForm({ data: values });

                    if (!result.isAxiosError) {
                        onCreate();
                        toggleShowModal();
                    }
                }}
            >
                <Form>
                    <CustomTextField label="Header" name="header" required />
                    <CustomTextField label="Paragrapgh" name="paragraph" required />
                    <CustomTextField label="On Off Rule" name="onOffRule" />
                    <CustomTextField label="Order Number" name="orderNumber" required />
                    <CustomToggle label="On Off Default" name="onOffDefault" />
                    <DialogFooter>
                        <PrimaryButton
                            text="Create"
                            type="submit"
                        />
                        <DefaultButton
                            loading={loading}
                            onClick={toggleShowModal}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Form>
            </Formik>
        </Dialog>
    );
};

const UpdateTipModal = ({
    showModal,
    toggleShowModal,
    onUpdate,
    courseId,
    course,
    selectedTipId
}) => {
    const [header, setHeader] = useState(undefined);
    const [paragraph, setParagraph] = useState(undefined);
    const [orderNumber, setOrderNumber] = useState(undefined);
    const [onOffDefault, setOnOffDefault] = useState(undefined);
    const [onOffRule, setOnOffRule] = useState(undefined);

    useEffect(() => {
        const tip = course?.tips.find(s => s.crsActionPlanTipID === selectedTipId);
        setHeader(tip?.header);
        setParagraph(tip?.paragraph);
        setOrderNumber(tip?.orderNumber);
        setOnOffDefault(tip?.onOffDefault);
        setOnOffRule(tip?.onOffRule);
    }, [selectedTipId])

    const [{ loading }, update] = useAxios(
        {
            method: "put",
            url: `/courses/${courseId}/action-plan-tip/${selectedTipId}`,
        },
        { manual: true }
    );

    if (loading)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Update",
                subText: "Tip"
            }}
        >
            <TextField label="Header" name="header" required defaultValue={header} onChange={(e, newValue) => setHeader(newValue)} />
            <TextField label="Paragraph" name="paragraph" required defaultValue={paragraph} onChange={(e, newValue) => setParagraph(newValue)} />
            <TextField label="On Off Rule" name="onOffRule" defaultValue={onOffRule} onChange={(e, newValue) => setOnOffRule(newValue)} />
            <TextField label="Order Number" name="orderNumber" required defaultValue={orderNumber} onChange={(e, newValue) => setOrderNumber(newValue)} />
            <Toggle label="On Off Default" name="onOffDefault" defaultChecked={onOffDefault} onChange={(e, newValue) => setOnOffDefault(newValue)} />
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await update({
                            data: {
                                crsActionPlanTipID: selectedTipId,
                                header,
                                paragraph,
                                orderNumber,
                                onOffDefault,
                                onOffRule,
                            }
                        });

                        if (!result.isAxiosError) {
                            onUpdate();
                            toggleShowModal();
                        }
                    }}
                    text="Update"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

const DeleteTipModal = ({
    showModal,
    toggleShowModal,
    onDelete,
    courseId,
    selectedTipId
}) => {

    const [{ loading }, deleteItem] = useAxios(
        {
            method: "delete",
            url: `/courses/${courseId}/action-plan-tip/${selectedTipId}`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Delete",
                subText: "Delete Tip?"
            }}
        >
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await deleteItem();

                        if (!result.isAxiosError) {
                            onDelete();
                            toggleShowModal();
                        }
                    }}
                    text="Delete"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

const modalCommon = {
    showModal: PropTypes.bool.isRequired,
    toggleShowModal: PropTypes.func.isRequired,
};

ActionPlanTipList.propTypes = {
    crsId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    revId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ])
};

CreateTipModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    onCreate: PropTypes.func.isRequired,
};

UpdateTipModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    course: PropTypes.shape({
        tips: PropTypes.arrayOf(PropTypes.shape({
            crsActionPlanTipID: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            header: PropTypes.string,
            paragraph: PropTypes.string,
            orderNumber: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            onOffDefault: PropTypes.bool,
            onOffRule: PropTypes.string
        }))
    }).isRequired,
    selectedTipId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    onUpdate: PropTypes.func.isRequired,
};

DeleteTipModal.propTypes = {
    ...modalCommon,
    courseId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    selectedTipId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    onDelete: PropTypes.func.isRequired,
};

export default ActionPlanTipList;